import { useEffect, useState } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/Dialog';
import { portalWsUrl } from '../lib/portal-ws-url';

export function TerminalSessionDialog(props: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  pathWithQuery: string | null;
}) {
  const { open, onClose, title, description, pathWithQuery } = props;
  const [termEl, setTermEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open || !pathWithQuery || !termEl) return;

    const term = new Terminal({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      theme: {
        background: '#111827',
        foreground: '#e5e7eb',
        cursor: '#e5e7eb',
      },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(termEl);
    fit.fit();

    const cols = term.cols || 120;
    const rows = term.rows || 40;
    const sep = pathWithQuery.includes('?') ? '&' : '?';
    const ws = new WebSocket(portalWsUrl(`${pathWithQuery}${sep}cols=${cols}&rows=${rows}`));

    ws.onmessage = (event) => {
      try {
        const frame = JSON.parse(String(event.data));
        if (frame?.type === 'output' && typeof frame.data === 'string') {
          term.write(frame.data);
        } else if (frame?.type === 'error' && typeof frame.message === 'string') {
          term.write(`\r\n[${frame.message}]\r\n`);
        } else if (frame?.type === 'exit') {
          term.write('\r\n[session closed]\r\n');
        }
      } catch {
        term.write(String(event.data));
      }
    };

    ws.onerror = () => {
      term.write('\r\n[connection error]\r\n');
    };

    term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'input', data }));
      }
    });

    const sendResize = () => {
      try { fit.fit(); } catch { /* ignore */ }
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
      }
    };
    const onWinResize = () => sendResize();
    window.addEventListener('resize', onWinResize);
    ws.onopen = () => sendResize();

    return () => {
      window.removeEventListener('resize', onWinResize);
      try { ws.close(); } catch { /* ignore */ }
      try { term.dispose(); } catch { /* ignore */ }
    };
  }, [open, pathWithQuery, termEl]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent maxWidth="4xl" className="flex flex-col h-[90vh] overflow-hidden w-full md:w-[80vw] md:max-w-[80vw]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <DialogBody className="flex-1 p-0 min-h-0 overflow-hidden">
          <div ref={setTermEl} className="h-full w-full bg-gray-900 p-2" />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
